import { ControlledRadio, ControlNumberField, ControlledMultiselectField } from 'components/Form';
import { ControlledTabs } from 'components/Form/ControlledTab';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { Viewport } from 'components/Viewport';
import { BimLimitedCostTypeEnum } from 'gql/graphql';
import type { IConstructionTypeListItem } from 'pages/Project/Common/utils';
import type { PropGetItemKey } from 'uikit';
import { cnMixSpace, Grid, GridItem, IconAlert } from 'uikit';
import { requiredBaseRule } from 'utils/constants';
import { replacerCostIfValue } from 'utils/replacerCost';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { useTypesLimitsFormValues } from '../hooks/useTypesLimitsFormValues';

import type { ItemGetKey, LimitType } from './types';
import {
    getLimitedCostRateValueDecimalScale,
    fotEnabledLimitedCostTypes,
    typesLimitedCost,
    bimLimitedCostCalcMethods,
    hiddenTooltipKey,
    selectedSubTabTooltipKey,
} from './types';

import Style from './BimContractEditLimitedCosts.module.css';

interface IBimContractEditLimitedCosts {
    control: Control;
    defaultValues?: any;
    classNameTooltip?: string;
    onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
}

const articlesItemKey: PropGetItemKey<ItemGetKey> = (item) => item?.type || '';

export const limitedCostsInformers = [
    {
        value: (
            <TooltipWrapper
                toolTipClassName={Style.informer}
                message="Затрата может быть отнесена либо только к лимитированным, либо только к прочим"
            >
                <IconAlert size="xs" view="warning" />
            </TooltipWrapper>
        ),
        itemId: BimLimitedCostTypeEnum.ShiftMethodOrganizationCost as unknown as number,
    },
];

const checkIsRegionalFactorRequired = (constructionTypeId: number, typesLimitsObj: Record<number, LimitType[]>) => {
    const typeWithRequired = [
        BimLimitedCostTypeEnum.SalarySupplementWorkingFarNorth,
        BimLimitedCostTypeEnum.SalarySupplementWorkingFarNorthEquatedAreas,
    ];

    return typesLimitsObj?.[constructionTypeId]?.some((l) => typeWithRequired?.includes(l?.type));
};

const regionalFactorRules = (constructionTypeId: number, typesLimitsObj: Record<number, LimitType[]>) => {
    const isRequired = checkIsRegionalFactorRequired(constructionTypeId, typesLimitsObj);

    return {
        validate: (val: null | string) => {
            const valueNum = replacerCostIfValue(val);

            if (valueNum && !isRequired && valueNum <= 0) {
                return 'Введите положительное значение районного коэффициента';
            }

            if (isRequired && (!valueNum || valueNum <= 0)) {
                return 'Поле обязательно для заполнения при выборе надбавки в поле "Статьи", введите положительное значение коэффициента';
            }

            return true;
        },
    };
};

export const BimContractEditLimitedCosts = (props: IBimContractEditLimitedCosts) => {
    const { control, classNameTooltip } = props;

    const ref = useRef<HTMLDivElement>(null);

    const constructionTypes = useWatch({ control, name: 'constructionTypes' });

    const constructionTypesFields: IConstructionTypeListItem[] = useMemo(
        () => constructionTypes?.map((c: IConstructionTypeListItem, index: number) => ({ ...c, index })),
        [constructionTypes],
    );

    const typesLimitsObj = useTypesLimitsFormValues(control);

    const [activeTab, setActiveTab] = useState(constructionTypesFields?.[0]);

    const onTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    useEffect(() => {
        if (constructionTypesFields && !activeTab) {
            setActiveTab(constructionTypesFields?.[0]);
        }
    }, [activeTab, constructionTypesFields]);

    const fieldsToCheck = useMemo(
        () =>
            constructionTypesFields.reduce((acc: any, ct) => {
                acc[ct.id] = [
                    `limitedCosts.regionalFactor_${ct.id}`,
                    `limitedCosts.typesLimitedCost_${ct.id}`,
                    ...(typesLimitsObj?.[ct.id]?.map(
                        (limitType) => `limitedCosts.rateValueLimitedCost_${ct.id}_${limitType.type}`,
                    ) || []),
                ];

                return acc;
            }, {}),
        [constructionTypesFields, typesLimitsObj],
    );

    const getTooltipClassName = useCallback(
        (constructionTypeId: number) =>
            `${classNameTooltip} ${activeTab?.id !== constructionTypeId ? hiddenTooltipKey : selectedSubTabTooltipKey}`,
        [activeTab?.id, classNameTooltip],
    );

    const onTooltipAttributeChange = useCallback((attributeName: string, target: Node) => {
        const { classList } = target as HTMLDivElement;

        const targetTop = parseInt((target as HTMLDivElement).style.getPropertyValue('--popover-top'), 10);
        const { top = 0, height = 0 } =
            ref?.current?.closest('.viewport-container')?.parentElement?.getBoundingClientRect() || {};

        if (!classList.contains(selectedSubTabTooltipKey) || !Number.isFinite(top) || !height) {
            return;
        }

        if (targetTop < top || targetTop > top + height) {
            if (!classList.contains(hiddenTooltipKey)) {
                classList.add(hiddenTooltipKey);
            }
        } else if (classList.contains(hiddenTooltipKey)) {
            classList.remove(hiddenTooltipKey);
        }
    }, []);

    return (
        <div ref={ref}>
            <ControlledRadio
                items={bimLimitedCostCalcMethods}
                control={control}
                name="limitedCostCalcMethod"
                direction="row"
                textLabel="Учет МТР Заказчика при расчете лимитированных затрат"
                textView="secondary"
                textSize="s"
                className={cnMixSpace({ mB: 'xl' })}
            />
            {constructionTypesFields?.length && (
                <>
                    <ControlledTabs
                        value={activeTab}
                        onChange={onTabChange}
                        items={constructionTypesFields}
                        view="bordered"
                        size="s"
                        fieldsToCheck={fieldsToCheck}
                        control={control}
                    />
                    <Viewport
                        marginBottom="var(--space-margin-bottom)"
                        shownChildIndex={activeTab?.index}
                        withScroll={false}
                    >
                        {constructionTypesFields.map((constructionType) => (
                            <Grid
                                rowGap="s"
                                cols="1"
                                className={cnMixSpace({ pV: 'm' })}
                                key={`limits_${constructionType.id}`}
                            >
                                <GridItem>
                                    <ControlNumberField
                                        name={`limitedCosts.regionalFactor_${constructionType.id}`}
                                        label="Районный коэффициент"
                                        labelPosition="left"
                                        control={control}
                                        size="s"
                                        type="number"
                                        width="full"
                                        decimalScale={2}
                                        rules={regionalFactorRules(constructionType.id, typesLimitsObj) as any}
                                        required={checkIsRegionalFactorRequired(constructionType.id, typesLimitsObj)}
                                        classNameTooltip={getTooltipClassName(constructionType.id)}
                                        onTooltipAttributeChange={onTooltipAttributeChange}
                                    />
                                </GridItem>
                                <GridItem>
                                    <ControlledMultiselectField
                                        label="Статьи"
                                        name={`limitedCosts.typesLimitedCost_${constructionType.id}`}
                                        control={control}
                                        labelPosition="left"
                                        items={typesLimitedCost}
                                        informers={limitedCostsInformers}
                                        getItemKey={articlesItemKey}
                                        classNameTooltip={getTooltipClassName(constructionType.id)}
                                        onTooltipAttributeChange={onTooltipAttributeChange}
                                    />
                                </GridItem>
                                {typesLimitsObj?.[constructionType.id] &&
                                    typesLimitsObj[constructionType.id].map((limitType) => (
                                        <GridItem
                                            key={`limitedCosts.rateValueLimitedCost${constructionType.id}${limitType.type}`}
                                            className={
                                                fotEnabledLimitedCostTypes.includes(limitType.type)
                                                    ? Style.fotEnabledRow
                                                    : undefined
                                            }
                                        >
                                            <ControlNumberField
                                                name={`limitedCosts.rateValueLimitedCost_${constructionType.id}_${limitType.type}`}
                                                label={
                                                    (limitType.type ===
                                                    BimLimitedCostTypeEnum.ShiftMethodOrganizationCost ? (
                                                        <>
                                                            {limitedCostsInformers[0].value}&nbsp;{limitType.label}, %
                                                        </>
                                                    ) : (
                                                        <>{limitType.label}, %</>
                                                    )) as unknown as string
                                                }
                                                labelPosition="left"
                                                placeholder="%"
                                                control={control}
                                                size="s"
                                                type="number"
                                                width={
                                                    !fotEnabledLimitedCostTypes.includes(limitType.type)
                                                        ? 'full'
                                                        : undefined
                                                }
                                                decimalScale={getLimitedCostRateValueDecimalScale(limitType.type)}
                                                rightSide="%"
                                                className={Style.rateValueLimitedCost}
                                                rules={requiredBaseRule}
                                                required
                                                classNameTooltip={getTooltipClassName(constructionType.id)}
                                                onTooltipAttributeChange={onTooltipAttributeChange}
                                            />
                                            {fotEnabledLimitedCostTypes.includes(limitType.type) && (
                                                <ControlNumberField
                                                    name={`limitedCosts.fotValueLimitedCost_${constructionType.id}_${limitType.type}`}
                                                    label="ФОТ, руб"
                                                    labelPosition="left"
                                                    placeholder="руб"
                                                    control={control}
                                                    size="s"
                                                    type="number"
                                                    decimalScale={0}
                                                    className={Style.fotValueLimitedCost}
                                                />
                                            )}
                                        </GridItem>
                                    ))}
                            </Grid>
                        ))}
                    </Viewport>
                </>
            )}
        </div>
    );
};

BimContractEditLimitedCosts.displayName = 'BimContractEditLimitedCosts';
